/**
 * VectorStylePanel — Card 4 §"Style: stroke / fill / icon / labels".
 *
 * Pure (state lifted to the caller). Renders token-driven swatch rows
 * for stroke and fill colors, a 4-step stroke-width selector, fill
 * opacity, a point-icon picker, and a labels toggle + label-from-field
 * selector. All controls disable under `readOnly` and surface a
 * `lock` icon with an explanatory tooltip per WP-4 acceptance.
 *
 * Source-of-truth citations:
 *   - geosoft-platform-design-system/docs/DESIGN_POLICY.md §5
 *   - geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md
 *   - docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §7.4
 */
import { useId } from 'react';
import { Icon, Stack, Text, Tooltip, tokens } from '@/design';
import {
  VECTOR_FILL_SWATCHES,
  VECTOR_ICON_SWATCHES,
  VECTOR_STROKE_SWATCHES,
  resolveSwatch,
  type VectorStyleState,
} from './vectorStyleModel';

export interface VectorStylePanelProps {
  readonly state: VectorStyleState;
  readonly availableLabelFields: ReadonlyArray<string>;
  readonly readOnly: boolean;
  readonly onChange: (next: VectorStyleState) => void;
  readonly 'data-testid'?: string;
}

const STROKE_WIDTH_OPTIONS = [1, 2, 3, 4] as const;
const FILL_OPACITY_OPTIONS = [0, 0.1, 0.2, 0.3, 0.5, 0.7] as const;

export function VectorStylePanel({
  state,
  availableLabelFields,
  readOnly,
  onChange,
  'data-testid': testId,
}: VectorStylePanelProps) {
  const headingId = useId();
  const strokePickerId = useId();
  const fillPickerId = useId();
  const widthPickerId = useId();
  const opacityPickerId = useId();
  const iconPickerId = useId();
  const labelFieldId = useId();

  const update = (patch: Partial<VectorStyleState>) => {
    if (readOnly) return;
    onChange({ ...state, ...patch });
  };

  const currentStroke = resolveSwatch(VECTOR_STROKE_SWATCHES, state.strokeSwatchId);
  const currentFill = resolveSwatch(VECTOR_FILL_SWATCHES, state.fillSwatchId);
  const currentIcon = VECTOR_ICON_SWATCHES.find((swatch) => swatch.id === state.iconSwatchId)
    ?? VECTOR_ICON_SWATCHES[0];

  return (
    <section
      aria-labelledby={headingId}
      data-testid={testId}
      style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[3] }}
    >
      <Stack direction="row" align="center" gap={2}>
        <Icon name="tune" size={20} decorative />
        <Text variant="subtitle" as="h3" id={headingId} style={{ margin: 0 }}>
          Symbology
        </Text>
        {readOnly ? (
          <Tooltip content="Read-only by project permission">
            <span
              aria-label="Read-only style controls"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
                color: tokens.color.ink.secondary,
              }}
            >
              <Icon name="lock" size={20} decorative />
            </span>
          </Tooltip>
        ) : null}
      </Stack>

      {/* Stroke swatch row */}
      <fieldset
        aria-labelledby={strokePickerId}
        disabled={readOnly}
        style={{
          margin: 0,
          padding: 0,
          border: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[2],
        }}
      >
        <legend
          id={strokePickerId}
          style={{
            ...textLegendStyle(),
            paddingInline: 0,
          }}
        >
          Stroke color
        </legend>
        <div role="radiogroup" aria-labelledby={strokePickerId} style={swatchRowStyle()}>
          {VECTOR_STROKE_SWATCHES.map((swatch) => (
            <SwatchOption
              key={swatch.id}
              swatch={swatch}
              kind="stroke"
              selected={swatch.id === state.strokeSwatchId}
              disabled={readOnly}
              onSelect={() => update({ strokeSwatchId: swatch.id })}
            />
          ))}
        </div>
        <Text variant="caption" as="span">
          Active stroke: <strong>{currentStroke.label}</strong>
        </Text>
      </fieldset>

      {/* Stroke width row */}
      <fieldset
        aria-labelledby={widthPickerId}
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <legend id={widthPickerId} style={textLegendStyle()}>
          Stroke width (px)
        </legend>
        <div role="radiogroup" aria-labelledby={widthPickerId} style={chipRowStyle()}>
          {STROKE_WIDTH_OPTIONS.map((width) => (
            <SegmentedChip
              key={width}
              label={`${width}`}
              icon="straighten"
              selected={width === state.strokeWidth}
              disabled={readOnly}
              onSelect={() => update({ strokeWidth: width })}
            />
          ))}
        </div>
      </fieldset>

      {/* Fill swatch row */}
      <fieldset
        aria-labelledby={fillPickerId}
        disabled={readOnly}
        style={{
          margin: 0,
          padding: 0,
          border: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[2],
        }}
      >
        <legend id={fillPickerId} style={textLegendStyle()}>
          Fill color
        </legend>
        <div role="radiogroup" aria-labelledby={fillPickerId} style={swatchRowStyle()}>
          {VECTOR_FILL_SWATCHES.map((swatch) => (
            <SwatchOption
              key={swatch.id}
              swatch={swatch}
              kind="fill"
              selected={swatch.id === state.fillSwatchId}
              disabled={readOnly}
              onSelect={() => update({ fillSwatchId: swatch.id })}
            />
          ))}
        </div>
        <Text variant="caption" as="span">
          Active fill: <strong>{currentFill.label}</strong>
        </Text>
      </fieldset>

      {/* Fill opacity */}
      <fieldset
        aria-labelledby={opacityPickerId}
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <legend id={opacityPickerId} style={textLegendStyle()}>
          Fill opacity
        </legend>
        <div role="radiogroup" aria-labelledby={opacityPickerId} style={chipRowStyle()}>
          {FILL_OPACITY_OPTIONS.map((opacity) => (
            <SegmentedChip
              key={opacity}
              label={`${Math.round(opacity * 100)}%`}
              icon="texture"
              selected={opacity === state.fillOpacity}
              disabled={readOnly}
              onSelect={() => update({ fillOpacity: opacity })}
            />
          ))}
        </div>
      </fieldset>

      {/* Point-icon picker (registry-aware) */}
      <fieldset
        aria-labelledby={iconPickerId}
        disabled={readOnly}
        style={{ margin: 0, padding: 0, border: 0, display: 'flex', flexDirection: 'column', gap: tokens.space[1] }}
      >
        <legend id={iconPickerId} style={textLegendStyle()}>
          Point icon (registry only)
        </legend>
        <div role="radiogroup" aria-labelledby={iconPickerId} style={chipRowStyle()}>
          {VECTOR_ICON_SWATCHES.map((swatch) => (
            <SegmentedChip
              key={swatch.id}
              label={swatch.label}
              icon={swatch.icon}
              selected={swatch.id === state.iconSwatchId}
              disabled={readOnly}
              onSelect={() => update({ iconSwatchId: swatch.id })}
            />
          ))}
        </div>
        <Text variant="caption" as="span">
          Selected glyph: <strong>{currentIcon?.label ?? 'Pin'}</strong>
        </Text>
      </fieldset>

      {/* Labels */}
      <Stack direction="column" gap={1}>
        <label
          htmlFor={`${labelFieldId}-toggle`}
          style={{ ...textLegendStyle(), display: 'flex', alignItems: 'center', gap: tokens.space[2] }}
        >
          <Icon name="label" size={20} decorative />
          Labels
        </label>
        <Stack direction="row" align="center" gap={2}>
          <input
            id={`${labelFieldId}-toggle`}
            type="checkbox"
            checked={state.labelsEnabled}
            disabled={readOnly}
            onChange={(event) => update({ labelsEnabled: event.target.checked })}
            style={{ accentColor: tokens.color.accent.primary }}
          />
          <Text variant="caption" as="span">
            {state.labelsEnabled ? 'Labels on' : 'Labels off'}
          </Text>
        </Stack>
        <label htmlFor={labelFieldId} style={textLegendStyle()}>
          Label from field
        </label>
        <select
          id={labelFieldId}
          value={state.labelField ?? ''}
          disabled={readOnly || !state.labelsEnabled}
          onChange={(event) =>
            update({ labelField: event.target.value === '' ? null : event.target.value })
          }
          style={selectStyle()}
        >
          <option value="">— Choose attribute —</option>
          {availableLabelFields.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </select>
      </Stack>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

interface SwatchOptionProps {
  readonly swatch: (typeof VECTOR_STROKE_SWATCHES)[number];
  readonly kind: 'stroke' | 'fill';
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
}

function SwatchOption({ swatch, kind, selected, disabled, onSelect }: SwatchOptionProps) {
  const baseStyle = {
    appearance: 'none',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.space[1],
    padding: tokens.space[2],
    minWidth: tokens.density.touchMin,
    minHeight: tokens.density.touchMin,
    background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
    border: selected
      ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
      : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.md,
    color: tokens.color.ink.primary,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  } as const;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={`${kind === 'stroke' ? 'Stroke' : 'Fill'} ${swatch.label}`}
      disabled={disabled}
      onClick={onSelect}
      style={baseStyle}
      title={swatch.hint ?? swatch.label}
    >
      <span
        aria-hidden="true"
        style={{
          width: tokens.space[5],
          height: tokens.space[3],
          background: swatch.value,
          borderRadius: tokens.radius.sm,
          border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        }}
      />
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[1],
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
        }}
      >
        <Icon name={swatch.icon} size={16} decorative />
        {swatch.label}
      </span>
    </button>
  );
}

interface SegmentedChipProps {
  readonly label: string;
  readonly icon: 'straighten' | 'texture' | 'location_on' | 'polyline' | 'pentagon' | 'flag' | 'verified';
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
}

function SegmentedChip({ label, icon, selected, disabled, onSelect }: SegmentedChipProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{
        appearance: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        minHeight: tokens.density.touchMin,
        background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
        border: selected
          ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
          : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.pill,
        color: tokens.color.ink.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        fontFamily: tokens.font.family.ui,
        fontSize: tokens.font.size.sm,
      }}
    >
      <Icon name={icon} size={20} decorative />
      <span>{label}</span>
    </button>
  );
}

function textLegendStyle() {
  return {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: tokens.font.role.label.weight,
    color: tokens.color.ink.secondary,
  } as const;
}

function swatchRowStyle() {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.space[2],
  } as const;
}

function chipRowStyle() {
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.space[1],
  } as const;
}

function selectStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
